import { REGIONS, YEARS, SORT_OPTIONS } from '@/mocks/movies';

export interface FilterState {
  genre: string;
  region: string;
  year: string;
  sort: string;
}

export const defaultFilters: FilterState = { genre: '全部', region: '全部', year: '全部', sort: 'latest' };

interface FilterBarProps {
  genres: string[];
  filters: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  showSort?: boolean;
}

function FilterRow({
  label,
  value,
  options,
  onSelect,
}: {
  label: string;
  value: string;
  options: string[];
  onSelect: (option: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start">
      <span className="shrink-0 pt-0.5 text-xs text-foreground-400 sm:w-12">{label}</span>
      <div className="flex flex-wrap gap-x-1 gap-y-1.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition ${
              value === option
                ? 'bg-primary-500 font-medium text-background-50'
                : 'text-foreground-700 hover:bg-background-100'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ genres, filters, onChange, showSort = true }: FilterBarProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 px-4 py-1 divide-y divide-background-100">
      <FilterRow label="类型" value={filters.genre} options={genres} onSelect={(genre) => onChange({ genre })} />
      <FilterRow label="地区" value={filters.region} options={REGIONS} onSelect={(region) => onChange({ region })} />
      <FilterRow label="年份" value={filters.year} options={YEARS} onSelect={(year) => onChange({ year })} />
      {showSort && (
        <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
          <span className="shrink-0 text-xs text-foreground-400 sm:w-12">排序</span>
          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange({ sort: option.value })}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition ${
                  filters.sort === option.value ? 'bg-background-50 font-medium text-primary-700 shadow-sm' : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function FilterSummary({ filters, onReset }: { filters: FilterState; onReset: () => void }) {
  const chips = [filters.genre, filters.region, filters.year].filter((value) => value !== '全部');
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <span className="text-[11px] text-foreground-400">已选条件</span>
      {chips.map((chip) => (
        <span key={chip} className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-900">{chip}</span>
      ))}
      <button type="button" onClick={onReset} className="whitespace-nowrap text-[11px] text-primary-700 underline-offset-2 transition hover:underline">
        重置筛选
      </button>
    </div>
  );
}